import React, { useCallback } from "react";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import TypingIndicator from "./TypingIndicator";
import useSpeechToText from "../../hooks/useSpeechToText";

export default function ChatInput({ onSend, value, onChange, isLoading }) {
  // Append each finalised phrase to the current textarea value.
  const handleTranscript = useCallback(
    (finalText) => {
      // onChange is React's setInput, which supports functional updates.
      onChange?.((prev) => (prev ? `${prev} ${finalText}` : finalText));
    },
    [onChange]
  );

  const { isListening, interimText, error, toggleListening, stopListening } = useSpeechToText({
    onTranscript: handleTranscript,
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading) {
        if (isListening) stopListening();
        onSend?.(value.trim());
      }
    }
  };

  return (
    <div className="px-4 pb-4 pt-3 bg-gray-50">
      <div className="relative mx-auto">
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about data assets, quality scores, domains, lineage..."
          rows={2}
          disabled={isLoading}
          className="w-full resize-none rounded-2xl border border-gray-300 bg-white px-4 py-4 pr-24 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-transparent shadow-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
        />

        {/* Mic + Send buttons */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <button
            onClick={toggleListening}
            disabled={isLoading}
            title={isListening ? "Stop listening" : "Start voice input"}
            className={`h-8 w-8  flex items-center justify-center rounded-lg border-none cursor-pointer transition-colors ${
              isListening
                ? "bg-red-100 text-red-600 animate-pulse"
                : "bg-transparent text-gray-500 hover:text-black"
            } disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isListening ? (
              <MicOffIcon className="text-xl" />
            ) : (
              <MicIcon className="text-xl" />
            )}
          </button>

          <button
            onClick={() => {
              if (value.trim() && !isLoading) {
                if (isListening) stopListening();
                onSend?.(value.trim());
              }
            }}
            disabled={isLoading}
            className="h-8 w-8 flex items-center justify-center rounded-lg bg-transparent border-none disabled:cursor-not-allowed cursor-pointer"
          >
            {isLoading ? (
              <TypingIndicator size="sm" />
            ) : (
              <SendIcon className="text-2xl !text-black" />
            )}
          </button>
        </div>
      </div>

      {/* Interim speech text — shown while recogniser is processing a phrase */}
      {isListening && interimText && (
        <p className="mt-1 px-2 text-xs text-gray-400 italic truncate">
          {interimText}…
        </p>
      )}

      {/* Listening indicator */}
      {isListening && !interimText && (
        <p className="mt-1 px-2 text-xs text-gray-400 italic">
          Listening…
        </p>
      )}

      {/* Speech error banner */}
      {error && (
        <p className="mt-1 px-2 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
